import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: cn(
          "h-12 bg-zinc-900 dark:bg-zinc-100",
          "hover:bg-zinc-800 dark:hover:bg-zinc-300",
          "text-white dark:text-zinc-900",
          "shadow-[0_1px_15px_rgba(0,0,0,0.1)]",
          "hover:shadow-[0_1px_20px_rgba(0,0,0,0.15)]",
          "font-medium text-sm"
        ),
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: cn(
          "h-12 bg-white dark:bg-zinc-900",
          "hover:bg-zinc-50 dark:hover:bg-zinc-800",
          "text-zinc-900 dark:text-zinc-100",
          "border border-zinc-200 dark:border-zinc-800",
          "hover:border-zinc-300 dark:hover:border-zinc-700",
          "shadow-sm hover:shadow-md",
          "text-sm font-medium"
        ),
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-primary hover:text-primary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
